// Path-based language routes for SEO landing pages.
// Mirrors /en/rent/ for ES/FR/DE/IT/NL so Google indexes clean URLs
// (query-param ?lang=xx variants are weak for ranking and often ignored).
// PT is the default (no prefix). The slug (equipment, district) stays
// PT-based for stable URLs across all languages.

import { siteBaseUrl } from '@/lib/siteDomain';

export const LANG_ROUTES = {
  pt: { seg: null,    rentWord: 'aluguer',    districtWord: 'distrito',  countyWord: 'concelho' },
  en: { seg: 'en',    rentWord: 'rent',       districtWord: 'district', countyWord: 'county' },
  es: { seg: 'es',    rentWord: 'alquilar',   districtWord: 'distrito', countyWord: 'concelho' },
  fr: { seg: 'fr',    rentWord: 'louer',      districtWord: 'district', countyWord: 'commune' },
  de: { seg: 'de',    rentWord: 'mieten',     districtWord: 'bezirk',   countyWord: 'kreis' },
  it: { seg: 'it',    rentWord: 'noleggiare', districtWord: 'distretto',countyWord: 'comune' },
  nl: { seg: 'nl',    rentWord: 'huren',      districtWord: 'district', countyWord: 'gemeente' },
};

export const PATH_LANGS = ['en', 'es', 'fr', 'de', 'it', 'nl'];

// All supported languages for hreflang generation (PT is default/no prefix).
export const ALL_LANGS = ['pt', 'en', 'es', 'fr', 'de', 'it', 'nl'];

// Build the clean canonical URL for an equipment (optionally + county) in a lang.
export function equipmentUrl(lang, equipmentSlug, countySlug = '') {
  const cfg = LANG_ROUTES[lang] || LANG_ROUTES.pt;
  const base = siteBaseUrl();
  if (!cfg.seg) {
    return `${base}/aluguer/${equipmentSlug}${countySlug ? `/${countySlug}` : ''}`;
  }
  return `${base}/${cfg.seg}/${cfg.rentWord}/${equipmentSlug}${countySlug ? `/${countySlug}` : ''}`;
}

// Build the clean canonical URL for a district in a lang.
export function districtUrl(lang, districtSlug) {
  const cfg = LANG_ROUTES[lang] || LANG_ROUTES.pt;
  const base = siteBaseUrl();
  if (!cfg.seg) {
    return `${base}/aluguer/distrito/${districtSlug}`;
  }
  return `${base}/${cfg.seg}/${cfg.rentWord}/${cfg.districtWord}/${districtSlug}`;
}

// Build the clean canonical URL for a county (municipality) page in a lang.
export function countyUrl(lang, countySlug) {
  const cfg = LANG_ROUTES[lang] || LANG_ROUTES.pt;
  const base = siteBaseUrl();
  if (!cfg.seg) {
    return `${base}/aluguer/${cfg.countyWord}/${countySlug}`;
  }
  return `${base}/${cfg.seg}/${cfg.rentWord}/${cfg.countyWord}/${countySlug}`;
}

// Detect the active language + route segments from the current pathname.
// Returns { lang, isLocalizedRoute, equipmentSlug, countySlug } for equipment
// pages, or { lang, ... } generally. Falls back to ?lang= legacy param.
export function detectRouteLang(pathname, searchParams) {
  const segments = pathname.split('/').filter(Boolean);
  for (const lang of PATH_LANGS) {
    const cfg = LANG_ROUTES[lang];
    if (segments[0] === cfg.seg && segments[1] === cfg.rentWord) {
      return {
        lang,
        isLocalizedRoute: true,
        segments,
        equipmentSlug: segments[2] || '',
        countySlug: segments[3] || '',
      };
    }
  }
  // PT default route: /aluguer/...
  if (segments[0] === 'aluguer') {
    const isDistrict = segments[1] === 'distrito';
    return {
      lang: 'pt',
      isLocalizedRoute: true,
      segments,
      isDistrict,
      equipmentSlug: isDistrict ? '' : (segments[1] || ''),
      countySlug: isDistrict ? '' : (segments[2] || ''),
      districtSlug: isDistrict ? (segments[2] || '') : '',
    };
  }
  // Legacy ?lang=xx fallback (still renders, but canonical points to path-based)
  const langParam = (searchParams && searchParams.get('lang')) || '';
  if (PATH_LANGS.includes(langParam)) {
    return { lang: langParam, isLocalizedRoute: false, segments, equipmentSlug: segments[1] || '', countySlug: segments[2] || '' };
  }
  return { lang: null, isLocalizedRoute: false, segments };
}